import { TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import Settings from "Data/Settings";
import * as Topics from "Data/Topics";
import * as Constants from "Data/Constants";
import { OutlineFilter } from "Filters/OutlineFilter";

export class Draggable extends HLF.Input.Draggable {

    public cancelSelectionWhenAnotherDragStarts: boolean = false;
    public _outlineFilter = new OutlineFilter(5, 0xffffff);
    protected sprite = this.visuals.addChild(new PIXI.Sprite());
    protected _subscriptions: any[] = [];
    protected _text: PIXI.BitmapText | undefined;
    protected _hoverVO: string;
    protected _clickCallback: ()=>void;

    constructor(
        texture: string | PIXI.Texture,
        anchor: PIXI.IPointData = {x: 0.5, y: 0.5},
        pHoverVO: string,
        pClickCallback: ()=>void,
    ) {
        super();
        this.setLayoutTexture(texture, anchor.x, anchor.y);
        this._hoverVO = pHoverVO;
        this._clickCallback = pClickCallback;
        this._subscriptions.push(
            PubSub.subscribe(HLF.DRAGGABLE_SELECTED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.DRAG_BEGIN, this.onMessageReceived.bind(this)),
        );
        this._visuals.filters = [this._outlineFilter];
        this._outlineFilter.enabled = false;
    }

    public destroy(pDestroyOptions?: any) {
        super.destroy(pDestroyOptions);
        this._subscriptions.forEach(PubSub.unsubscribe);
    }

    public setLayoutTexture(texture: string | PIXI.Texture, anchorX: number, anchorY: number, spritesheet?: string | string[]) {
        if(typeof texture === 'string') {
            texture = texture.replace(/\.png$/, '');
            this.name = texture;
        }
        this.sprite.texture = (texture instanceof PIXI.Texture ? texture :
            texture ? HLF.PixiUtils.getTexture(texture, spritesheet) : PIXI.Texture.EMPTY) || PIXI.Texture.EMPTY;
        this.sprite.anchor.x = anchorX;
        this.sprite.anchor.y = anchorY;
        this.hitArea = this.sprite.hitArea;
    }

    public addText(pText: string, pFont: string, pFontSize: number | string, pColor: number = 0x000000): void {
        this._text = new PIXI.BitmapText(pText, {
            fontName: pFont,
            fontSize: typeof pFontSize === "number" ? pFontSize : parseInt(pFontSize, 10),
            align: "centre",
            tint: pColor,
        });
        this._text.position.set(0, 0);
        this._text.anchor = 0.5;
        this._visuals.addChild(this._text);
    }

    /**
     * Change the text of the button. Make sure to call `addText` first.
     * @param pText The text to be displayed.
     */
    public changeText(pText: string) {
        if (this._text !== undefined) {
            this._text.text = pText;
        }
    }

    public getFocusPosition(): PIXI.Point {
        return this.sprite.getGlobalPosition();
    }

    public onFocusBegin(): void {
        super.onFocusBegin();
        this.playHoverVo();
        this.enableOutline();
    }

    public onFocusEnd(): void {
        super.onFocusEnd();
        this.disableOutline();
    }

    public onFocusActivated(): void {
        super.onFocusActivated();
        this._clickCallback();
    }

    public dragBegin(): void {
        super.dragBegin();
    }

    protected enableOutline() {
        this._outlineFilter.enabled = true;
    }

    protected disableOutline() {
        this._outlineFilter.enabled = false;
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case HLF.DRAGGABLE_SELECTED:
            case HLF.DRAG_BEGIN:
                // cancel selection when another drag starts
                if (pData !== this && this.cancelSelectionWhenAnotherDragStarts) {
                    this._visuals.position.set(0);
                    this.deselect();
                }
                break;
        }
    }

    /**
     * onPointerOver
     */
    protected onPointerOver(pEvent: PIXI.InteractionEvent): void {
        super.onPointerOver(pEvent);
        this.playHoverVo();
        this.enableOutline();
    }

    /**
     * onPointerDown
     */
    protected onPointerDown(pEvent: PIXI.InteractionEvent): void {
        super.onPointerDown(pEvent);
        this.enableOutline();
    }

    /**
     * onPointerUp
     */
    protected onPointerUp(pEvent: PIXI.InteractionEvent): void {
        super.onPointerUp(pEvent);
        if (HLF.PlatformUtils.isMobile()) {
            this.disableOutline();
        }
    }

    /**
     * onPointerUpOutside
     */
    protected onPointerUpOutside(pEvent: PIXI.InteractionEvent): void {
        super.onPointerUpOutside(pEvent);
        this.disableOutline();
    }

    /**
     * onPointerOut
     */
    protected onPointerOut(pEvent: PIXI.InteractionEvent): void {
        super.onPointerOut(pEvent);
        if (!this.isDragging) {
            this.disableOutline();
        }
    }

    protected playHoverVo(): void {
        // don't call super.playHoverVo();
        HLF.Application.instance.voiceover.playVO(this._hoverVO, { priority: -1, mode: HLF.PlayMode.Override });
    }
}
